import React, { FC } from 'react'
import { styled, useTheme } from '@mui/material/styles'
import { ButtonBase, Stack, TextField, Typography } from '@mui/material'
import { CSSProperties } from 'react'
import { BASIC_RESULT_LABEL } from '../../constants/const'
import { Clipboard } from '../templates/Clipboard'
import { localStorageWrapper } from '../../helpers/localStorage'

type Props = {
  label: string
  value: string
  maxWidth?: CSSProperties['maxWidth']
  hasHighCheckPriority?: boolean
  onClick: () => void
  isHighlighted: boolean
  isShrinkForbidden?: boolean
  isTall?: boolean
}

const StyledInputBox = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'isTall',
})<{ isTall: boolean }>({
  '& .MuiOutlinedInput-root': {
    height: '32px',
    borderRadius: '0',
  },
  width: '100%',
  '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
    transform: 'translate(20%, 20%)',
  },
  variants: [
    {
      props: ({ isTall }) => isTall,
      style: {
        '& .MuiOutlinedInput-root': {
          height: '48px',
        },
      },
    },
    {
      props: ({ isTall }) => isTall,
      style: {
        '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
          transform: 'translate(20%, 45%)',
        },
      },
    },
  ],
})

export const HighlightInputBox: FC<Props> = ({
  label,
  value,
  hasHighCheckPriority,
  maxWidth,
  onClick,
  isHighlighted,
  isShrinkForbidden = false,
  isTall = false,
}) => {
  const theme = useTheme()
  const inputBoxColor = isHighlighted
    ? theme.palette.primary.dark
    : theme.palette.secondary.main

  const isNameAbleToCopy = localStorageWrapper.getItem('is_name_able_to_copy')

  return (
    <Stack direction="row" spacing={1}>
      <ButtonBase style={{ width: '100%', maxWidth: maxWidth }}>
        <StyledInputBox
          isTall={isTall}
          label={isShrinkForbidden && value ? '' : label}
          value={value}
          onClick={onClick}
          style={{
            maxWidth: maxWidth,
            backgroundColor: hasHighCheckPriority
              ? theme.palette.warning.light
              : '#fff',
          }}
          InputProps={{
            endAdornment: isHighlighted && <Typography>✔️</Typography>,
            readOnly: true,
          }}
          InputLabelProps={{
            sx: {
              color: inputBoxColor,
              '&.Mui-focused': {
                color: inputBoxColor,
              },
            },
          }}
          sx={[
            {
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: inputBoxColor,
                },
              },
              '& .MuiOutlinedInput-root.Mui-focused': {
                '& > fieldset': {
                  borderColor: inputBoxColor,
                },
              },
            },
            isHighlighted
              ? {
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderWidth: '2px',
                    },
                  },
                }
              : {
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderWidth: '1px',
                    },
                  },
                },
          ]}
        />
      </ButtonBase>
      {isNameAbleToCopy &&
        BASIC_RESULT_LABEL['id_11_patient_name_kana'] === label && (
          <Clipboard value={value} />
        )}
    </Stack>
  )
}
